import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SmilePlus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Reaction {
  id: string;
  user_id: string;
  reaction: string;
}

interface MessageReactionsProps {
  messageId: string;
  reactions: Reaction[];
  onReactionUpdate: () => void;
}

const AVAILABLE_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

export const MessageReactions = ({
  messageId,
  reactions,
  onReactionUpdate,
}: MessageReactionsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  // Group reactions by type and count
  const groupedReactions = reactions.reduce((acc, r) => {
    if (!acc[r.reaction]) {
      acc[r.reaction] = { count: 0, users: [] };
    }
    acc[r.reaction].count++;
    acc[r.reaction].users.push(r.user_id);
    return acc;
  }, {} as Record<string, { count: number; users: string[] }>);

  const toggleReaction = async (emoji: string) => {
    if (!user) return;

    try {
      // Check if user already reacted with this emoji
      const existingReaction = reactions.find(
        (r) => r.user_id === user.id && r.reaction === emoji
      );

      if (existingReaction) {
        // Remove reaction
        const { error } = await supabase
          .from('message_reactions')
          .delete()
          .eq('id', existingReaction.id);

        if (error) throw error;
      } else {
        // Add reaction
        const { error } = await supabase
          .from('message_reactions')
          .insert({
            message_id: messageId,
            user_id: user.id,
            reaction: emoji,
          });

        if (error) throw error;
      }

      onReactionUpdate();
      setOpen(false);
    } catch (error: any) {
      console.error('Error toggling reaction:', error);
      toast({
        title: 'Error',
        description: 'Failed to update reaction',
        variant: 'destructive',
      });
    }
  };

  const hasUserReacted = (emoji: string) => {
    return reactions.some((r) => r.user_id === user?.id && r.reaction === emoji);
  };

  return (
    <div className="flex items-center gap-1 mt-1">
      {/* Existing reactions */}
      {Object.entries(groupedReactions).map(([emoji, data]) => (
        <button
          key={emoji}
          onClick={() => toggleReaction(emoji)}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
            data.users.includes(user?.id || '')
              ? 'bg-primary/20 border border-primary/40'
              : 'bg-muted hover:bg-muted/70 border border-border'
          }`}
        >
          <span>{emoji}</span>
          <span className="text-[10px]">{data.count}</span>
        </button>
      ))}

      {/* Add reaction button */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 rounded-full"
          >
            <SmilePlus className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="flex gap-1">
            {AVAILABLE_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => toggleReaction(emoji)}
                className={`text-2xl p-2 rounded-lg hover:bg-muted transition-colors ${
                  hasUserReacted(emoji) ? 'bg-primary/20' : ''
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
